import { observer } from "mobx-react-lite";
import { useMst } from "../../stores/main";
import { modsDefinitions } from "./mod-definitions";
import { Space } from "antd";
import { type CSSProperties, useEffect, useMemo, useRef } from "react";
import { PanelHeader } from "./ModsListPanelHeader";
import "./ModsList.scss";
import "./modForms/ModForm.scss";
import { ErrorBoundary } from "../ErrorBoundary/ErrorBoundary";
import {
  DndContext,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { IModModel } from "../../stores/ModsStore";
import {
  buildAssetBindingKey,
  buildConnectorLayerInspectorSelections,
  buildLayerBindingKeys,
  buildLayerInspectorKeySet,
  collectAssetBindings,
  collectLayerBindings,
} from "../../helpers/layerSelectionUtils";
import {
  clearLayerInspectorHover,
  setLayerInspectorHover,
} from "../../helpers/layerInspectorTools";
import { recordStudioPerf, studioPerfNow } from "../../helpers/studioPerfDebug";
import { ReactComponent as ConnectIcon } from "../../icons-and-animations/layer-inspector/connect.svg";

const getLayerKeysForMod = (mod: IModModel): string[] => {
  const startedAt = studioPerfNow();
  let layerKeys: string[] = [];
  try {
    if (!mod?.moddata) return layerKeys;
    const keys = new Set<string>();
    collectLayerBindings(mod.moddata).forEach((binding) => {
      buildLayerBindingKeys(binding).forEach((key) => {
        if (key) keys.add(key);
      });
    });
    collectAssetBindings(mod.moddata).forEach((binding) => {
      const key = buildAssetBindingKey(binding);
      if (key) keys.add(key);
    });
    layerKeys = Array.from(keys);
    return layerKeys;
  } finally {
    recordStudioPerf(
      "ModsList.getLayerKeysForMod",
      studioPerfNow() - startedAt,
      {
        keyCount: layerKeys.length,
        modId: mod?.id,
        type: mod?.moddata?.type,
      }
    );
  }
};

const hasMatchingLayerKey = (
  keySet: Set<string>,
  layerKeys: string[]
): boolean => keySet.size > 0 && layerKeys.some((key) => keySet.has(key));

const SortableModItem = observer(
  ({
    mod,
    index,
    handleSelect,
    selectionLayerKeys,
    hoveredLayerKeys,
  }: {
    mod: IModModel;
    index: number;
    handleSelect: (key?: string) => void;
    selectionLayerKeys: Set<string>;
    hoveredLayerKeys: Set<string>;
  }) => {
    const renderStartedAt = studioPerfNow();
    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging,
    } = useSortable({ id: mod.id.toString() });
    const {
      modsStore: {
        updateValue,
        removeMod,
        copyMod,
        modsWithMultipleDynamicLength,
        modsWithDataErrors,
        modsWithMissingProperties,
        modsWithBrokenLayers,
        activeModId,
        hoveredControlModId,
        hasMultiConnectedLayers,
      },
      playerStore: {
        jsonFile,
        layerInspectorAvailable,
        layerInspectorEnabled,
        layerInspectorMode,
        mainAssetKey,
      },
      experimentStore: { deleteExperimentById },
    } = useMst();

    const { id, moddata, name } = mod;
    // Early return if moddata is missing (shouldn't happen due to filter, but defensive check)
    if (!moddata?.type) {
      recordStudioPerf(
        "ModsList.SortableModItem render skipped",
        studioPerfNow() - renderStartedAt,
        { modId: id }
      );
      return null;
    }
    const isSelected = activeModId === id.toString();
    const isHoveredFromLayerPanel = hoveredControlModId === id.toString();
    const hasMultiConnectedLayer = hasMultiConnectedLayers(id);
    const dataErrors = modsWithDataErrors[id] || [];
    const missingPropertyErrors = modsWithMissingProperties[id] || [];
    const multipleDynamicLengthErrors = modsWithMultipleDynamicLength[id] || [];
    const brokenLayerErrors = modsWithBrokenLayers[id] || [];
    const hasMissingProperty = missingPropertyErrors.length > 0;
    const hasDataErrors = dataErrors.length > 0;
    const hasMultipleDynamicLength = multipleDynamicLengthErrors.length > 0;
    const hasBrokenLayers = brokenLayerErrors.length > 0;
    if (!modsDefinitions[moddata.type]) {
      recordStudioPerf(
        "ModsList.SortableModItem render skipped",
        studioPerfNow() - renderStartedAt,
        { modId: id, type: moddata.type }
      );
      return null;
    }
    const styleTransform = {
      transform: CSS.Transform.toString(transform),
      transition,
      zIndex: isDragging ? 999 : undefined,
    };
    const { color, formTitle, formTitle2, text } =
      modsDefinitions[moddata.type] || {};
    const itemStyle = {
      position: "relative",
      "--my-dynamic-color": color,
      ...styleTransform,
    } as CSSProperties;

    const layerKeys = getLayerKeysForMod(mod);
    const matchesHoveredLayer =
      !moddata.isDisabled && hasMatchingLayerKey(hoveredLayerKeys, layerKeys);
    const matchesSelectedLayer =
      !moddata.isDisabled &&
      !matchesHoveredLayer &&
      hasMatchingLayerKey(selectionLayerKeys, layerKeys);
    const highlightClass = isHoveredFromLayerPanel
      ? "layer-control-hover-highlighted"
      : matchesHoveredLayer
      ? "layer-hover-highlighted"
      : matchesSelectedLayer
      ? "layer-selected-highlighted"
      : "";

    const panelKey = id.toString();
    const canHoverInPlayer =
      layerInspectorAvailable &&
      layerInspectorEnabled &&
      layerInspectorMode === "connect" &&
      !moddata.isDisabled;

    recordStudioPerf(
      "ModsList.SortableModItem render",
      studioPerfNow() - renderStartedAt,
      {
        disabled: Boolean(moddata.isDisabled),
        hoverMatch: matchesHoveredLayer,
        modId: id,
        selected: isSelected,
        selectionMatch: matchesSelectedLayer,
        type: moddata.type,
      }
    );

    const handleConnectorMouseEnter = () => {
      if (!canHoverInPlayer) return;

      const [selection] = buildConnectorLayerInspectorSelections(
        moddata,
        jsonFile,
        { fallbackAssetName: mainAssetKey }
      );
      if (selection) {
        setLayerInspectorHover(selection);
      } else {
        clearLayerInspectorHover();
      }
    };

    const handleConnectorMouseLeave = () => {
      if (!canHoverInPlayer) return;
      clearLayerInspectorHover();
    };

    return (
      <div
        ref={setNodeRef}
        {...attributes}
        style={itemStyle}
        className={`mod-list-item ${moddata.isDisabled ? "disabled" : ""} ${
          moddata.type
        } ${highlightClass} ${isSelected ? "is-selected" : ""}`}
        id={`connector-${index.toString()}`}
        onMouseEnter={handleConnectorMouseEnter}
        onMouseLeave={handleConnectorMouseLeave}
      >
        <PanelHeader
          listeners={listeners}
          onChange={(i, k, v) => {
            updateValue(id, k, v);
          }}
          handleRemove={() => {
            if (mod.moddata.experimentId)
              deleteExperimentById(mod.moddata.experimentId);
            removeMod(mod);
          }}
          handleCopy={() => {
            copyMod(mod);
          }}
          formTitleText={formTitle(moddata)}
          formTitleText2={formTitle2 && formTitle2(moddata)}
          modTitle={text.toUpperCase()}
          modName={name}
          hasMultiConnectedLayer={hasMultiConnectedLayer}
          hasMissingProperties={hasMissingProperty}
          hasDataErrors={hasDataErrors}
          dataErrors={dataErrors}
          modId={panelKey}
          hasBrokenLayers={hasBrokenLayers}
          brokenLayerErrors={brokenLayerErrors}
          type={moddata.type}
          isDisabled={moddata.isDisabled}
          setConnectorDisabledState={(state) => {
            updateValue(id, "isDisabled", state);
          }}
          setActiveMod={handleSelect}
          isActiveMod={isSelected}
          hasMultipleDynamicLength={hasMultipleDynamicLength}
          multipleDynamicLengthErrors={multipleDynamicLengthErrors}
          missingPropertyErrors={missingPropertyErrors}
        />
      </div>
    );
  }
);

export const ModsList = observer(() => {
  const renderStartedAt = studioPerfNow();
  const {
    modsStore: {
      mods,
      setActiveMod,
      addCbAfterAddingMod,
      snapshotMods,
      reorderMods,
    },
    platformStore: { isLoadingSelectedProject, selectedVideoPart },
    playerStore: {
      layerInspectorSelections,
      layerInspectorHoveredLayers,
      loadedJsonSnapshot,
      loadedJsonUrl,
    },
  } = useMst();

  const visibleMods = mods.filter((mod) => mod?.moddata?.type);
  const isSelectedSceneJsonLoaded =
    Boolean(loadedJsonSnapshot) &&
    !isLoadingSelectedProject &&
    (!selectedVideoPart?.jsonUrl || loadedJsonUrl === selectedVideoPart.jsonUrl);
  const shouldShowEmptyState =
    visibleMods.length === 0 && isSelectedSceneJsonLoaded;
  const reset = () => {
    snapshotMods();
  };

  const collapseScrollContainerRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    addCbAfterAddingMod((id) => {
      setTimeout(() => {
        try {
          collapseScrollContainerRef?.current?.scrollTo({
            top: 0,
            behavior: "smooth",
          });
        } catch (e) {}
      }, 80);
    });
  }, [addCbAfterAddingMod]);

  const selectionLayerKeys = useMemo(
    () => buildLayerInspectorKeySet(layerInspectorSelections),
    [layerInspectorSelections]
  );
  const hoveredLayerKeys = useMemo(
    () => buildLayerInspectorKeySet(layerInspectorHoveredLayers),
    [layerInspectorHoveredLayers]
  );

  const sensors = useSensors(useSensor(PointerSensor));

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      const modsArray = [...mods];
      const activeIndex = modsArray.findIndex(
        (mod) => mod.id.toString() === active.id
      );
      const overIndex = modsArray.findIndex(
        (mod) => mod.id.toString() === over.id
      );
      const [reorderedMod] = modsArray.splice(activeIndex, 1);
      modsArray.splice(overIndex, 0, reorderedMod);
      reorderMods(modsArray);
    }
  };

  const handleSelectChange = (key?: string) => {
    setActiveMod(key);
  };

  recordStudioPerf("ModsList render", studioPerfNow() - renderStartedAt, {
    hoveredKeyCount: hoveredLayerKeys.size,
    selectionKeyCount: selectionLayerKeys.size,
    shouldShowEmptyState,
    visibleMods: visibleMods.length,
  });

  return (
    <ErrorBoundary resetFunc={reset}>
      <div className="ModsList-wrapper">
        <DndContext sensors={sensors} onDragEnd={handleDragEnd}>
          <SortableContext
            items={visibleMods.map((mod) => mod.id.toString())}
            strategy={verticalListSortingStrategy}
          >
            <Space
              direction="vertical"
              style={{ overflow: "hidden" }}
              className="ModsList-container"
            >
              <div
                id="connectors-list"
                className="modsList-collapse-container"
                ref={collapseScrollContainerRef}
              >
                <div className="ModsList-stack">
                  {shouldShowEmptyState && (
                    <div className="ModsList-empty">
                      <p>
                        Connectors let you add dynamic logic and interactivity
                        to your content.
                      </p>
                      <p>
                        Use connectors to replace static layer values with
                        dynamic data, so each video can show different text,
                        media, colors, design properties, or other custom
                        changes.
                      </p>
                      <p>
                        You can also make visual layers act as buttons, to open
                        a link, play another scene and navigate inside the
                        video, or add in-video inputs.
                      </p>
                      <p>
                        To create a connector, click the + button above, or
                        select a layer in the player and click the{" "}
                        <ConnectIcon
                          aria-label="Connect"
                          className="ModsList-empty__connect-icon"
                        />{" "}
                        button.
                      </p>
                    </div>
                  )}
                  {visibleMods.map((mod, index) => (
                    <SortableModItem
                      handleSelect={handleSelectChange}
                      key={mod.id.toString()}
                      mod={mod}
                      index={index}
                      selectionLayerKeys={selectionLayerKeys}
                      hoveredLayerKeys={hoveredLayerKeys}
                    />
                  ))}
                </div>
              </div>
            </Space>
          </SortableContext>
        </DndContext>
      </div>
    </ErrorBoundary>
  );
});
